<script setup lang="ts">
import {
  AITypeList,
  ChatStatus,
  type IChat,
  type IAINotification,
  AINotificationKeyCount,
  AINotificationTimeOut,
  AINotificationStopTimeOut,
  AINotificationList
} from '@/utils/jDroid'
import { IDEVIEWTABS_MOBILE } from '@/utils/tabs'
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

import jdroidService from '@/services/ide/jdroid.service'
import { useIdeStore } from '@/stores/ide.store'
import { useJdroidStore } from '@/stores/jdroid.store'

import Button from '@/components/atoms/button/index.vue'

const ideStore = useIdeStore()
const jdroidStore = useJdroidStore()

const refInterval = ref<NodeJS.Timeout | number>(0)
const notificationList = ref<IAINotification>(AINotificationList[0])
const stopNotification = ref<boolean>(false)

const isDefaultIde = computed(() => ideStore.isDefaultIde)
const isBlockly = computed(() => ideStore.isBlockly)

const isJdroid = computed(() => {
  return !(isBlockly.value || isDefaultIde.value)
})
const showJdroid = computed(() => {
  return jdroidStore.showJdroid
})
const currentMobileTab = computed(() => {
  return ideStore.currentMobileTab
})
const showNotification = computed(() => {
  return jdroidStore.showNotification
})
const notificationKeyCount = computed(() => {
  return jdroidStore.notificationKeyCount
})
/**
 * show the notification
 */
const showNotificationModel = () => {
  if (showNotification.value) {
    if (refInterval.value) clearTimeout(refInterval.value)
  } else {
    notificationList.value =
      AINotificationList[Math.floor(Math.random() * AINotificationList.length)]
    jdroidStore.setShowNotification(true)
    resetJdroidNotification()
  }
}
/**
 * Toggles the jdroid
 */
const toggleNotificationTimeout = () => {
  if (refInterval.value) clearTimeout(refInterval.value)
  refInterval.value = setTimeout(() => {
    showNotificationModel()
  }, AINotificationTimeOut)
}

/**
 * Toggles the jdroid
 */
const resetJdroidNotification = () => {
  jdroidStore.setNotificationKeyCount(0)
  if (refInterval.value) clearTimeout(refInterval.value)
}
/**
 * hide the notification
 */
const hideJdroidNotification = () => {
  stopNotification.value = true
  jdroidStore.setShowNotification(false)
  clearTimeout(refInterval.value)

  setTimeout(() => {
    stopNotification.value = false
    resetJdroidNotification()
  }, AINotificationStopTimeOut)
}
/**
 * Accept the notification
 * @param notificationType string
 */
const acceptNotification = (notificationType: string) => {
  const type = AITypeList.find((item) => item.type === notificationType)

  if (type) {
    const chat: IChat = {
      type: type.type,
      autoReplaceEditor: false,
      ideContent: null,
      outputContent: null,
      header: type.header,
      status: ChatStatus.LOADING,
      response: null
    }
    jdroidService.addChat(chat)
    if (currentMobileTab.value !== null) {
      ideStore.setMobileTab(IDEVIEWTABS_MOBILE.JDROID)
    }
  }
}

onMounted(() => {
  resetJdroidNotification()
  if (currentMobileTab.value === null && !showJdroid.value) {
    resetJdroidNotification()
  }
  if (currentMobileTab.value === IDEVIEWTABS_MOBILE.IDE) {
    resetJdroidNotification()
  }
  watch(showJdroid, (newVal) => {
    if (!newVal && currentMobileTab.value === null) {
      resetJdroidNotification()
    } else {
      if (refInterval.value) clearTimeout(refInterval.value)
      jdroidStore.setShowNotification(false)
    }
  })
  watch(currentMobileTab, (newVal) => {
    if (newVal === IDEVIEWTABS_MOBILE.IDE) {
      resetJdroidNotification()
    } else {
      if (newVal === null && !showJdroid.value) {
        resetJdroidNotification()
      } else {
        if (refInterval.value) clearTimeout(refInterval.value)
        jdroidStore.setShowNotification(false)
      }
    }
  })
  watch(notificationKeyCount, (newVal) => {
    if (stopNotification.value) return

    if (showJdroid.value === false) {
      if (newVal === 0) {
        if (refInterval.value) clearTimeout(refInterval.value)
      } else if (newVal < AINotificationKeyCount) {
        toggleNotificationTimeout()
      } else {
        if (!showNotification.value) showNotificationModel()
      }
    } else {
      if (refInterval.value) clearTimeout(refInterval.value)
    }
  })
})

onUnmounted(() => {
  if (refInterval.value) clearTimeout(refInterval.value)
})
</script>

<template>
  <div
    v-if="isJdroid && showNotification"
    class="section-primary border-primary absolute right-4 z-10 inline-flex h-fit w-fit flex-col items-start justify-end gap-4 rounded-bl-xl rounded-tr-lg border p-2.5"
  >
    <span class="class text-justify text-xs font-medium leading-tight"
      >{{ notificationList.message }}
    </span>
    <div class="inline-flex h-7 w-full items-end justify-start gap-1">
      <Button
        :id="notificationList?.id"
        color="secondary"
        variant="filled"
        size="small"
        class="p-2 text-xs"
        @click="acceptNotification(notificationList.type)"
      >
        {{ notificationList.buttonText }}
      </Button>
      <Button
        class="p-2 text-xs"
        color="secondary"
        variant="link"
        size="small"
        @click="hideJdroidNotification()"
      >
        Not Now
      </Button>
    </div>
  </div>
</template>
